import { useEffect, useMemo, useState } from 'react';
import { Bug, Camera, CheckCircle2, FileWarning, RefreshCw, Send } from 'lucide-react';

const SECTIONS = [
  ['login_registration', 'Login / Registration'],
  ['street', 'Street'],
  ['trade', 'Trade / Market'],
  ['travel', 'Travel'],
  ['home', 'Home / Safehouse'],
  ['scene_encounter', 'Scene Encounter'],
  ['street_log_notifications', 'Street Log / Notifications'],
  ['account_settings', 'Account Settings'],
  ['mobile_layout', 'Mobile Layout'],
  ['other', 'Other'],
];

const BUG_TYPES = [
  ['wrong_information', 'Wrong information shown'],
  ['button_no_action', 'Button or action does nothing'],
  ['action_failed', 'Action failed'],
  ['layout_display', 'Layout or display problem'],
  ['state_not_updating', 'Game state not updating'],
  ['connection_server', 'Connection or server error'],
  ['gameplay_rule', 'Possible gameplay-rule bug'],
  ['other', 'Other'],
];

const FREQUENCIES = [
  ['once', 'Happened once'],
  ['sometimes', 'Happens sometimes'],
  ['every_time', 'Happens every time'],
];

const STATUS_LABELS = {
  submitted: 'Submitted',
  under_review: 'Under review',
  needs_information: 'Need more information',
  confirmed: 'Confirmed',
  fixed: 'Fixed',
  closed: 'Closed',
};

function initialForm() {
  return {
    section: 'other', bug_type: 'state_not_updating', frequency: 'once',
    title: '', description: '', steps_to_reproduce: '', expected_result: '', actual_result: '',
  };
}

function readScreenshot(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      reject(new Error('Screenshot must be PNG, JPEG, or WebP.'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('Screenshot must be 10 MB or smaller.'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Screenshot could not be read.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(file);
  });
}

export function BugReportForm({ hub, bugContext = {} }) {
  const [form, setForm] = useState(initialForm);
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsBusy, setReportsBusy] = useState(false);

  const diagnosticsPreview = useMemo(() => ({
    client: bugContext.client_version || 'unknown',
    interface: bugContext.interface || 'unknown',
    theme: bugContext.theme || 'unknown',
    location: bugContext.current_location || 'unknown',
    run: bugContext.run_id || 'none',
    revision: bugContext.run_revision ?? 'none',
  }), [bugContext]);

  const loadReports = async () => {
    setReportsBusy(true);
    try {
      const response = await hub.bugReports();
      setReports(response?.bug_reports || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReportsBusy(false);
    }
  };

  useEffect(() => { loadReports(); }, []);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const update = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const chooseScreenshot = (event) => {
    const file = event.target.files?.[0] || null;
    setError('');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setScreenshot(file);
    setPreviewUrl(file ? URL.createObjectURL(file) : '');
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSuccess(null);
    try {
      const screenshotData = await readScreenshot(screenshot);
      const response = await hub.submitBugReport({
        ...form,
        client_version: bugContext.client_version || '',
        diagnostics: bugContext,
        screenshot_data: screenshotData,
      });
      setSuccess(response.bug_report);
      setForm(initialForm());
      setScreenshot(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      await loadReports();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-section bug-support-section">
      <div className="bug-support-heading">
        <div><h3><Bug size={18} /> Submit Bug</h3><p>Send a structured report directly to the hub. A screenshot is optional.</p></div>
        <button type="button" className="mini-button" onClick={loadReports} disabled={reportsBusy}><RefreshCw size={14} /> Refresh</button>
      </div>

      <form className="bug-report-form" onSubmit={submit}>
        <div className="preference-grid bug-select-grid">
          <label>Section<select value={form.section} onChange={(event) => update('section', event.target.value)} disabled={busy}>{SECTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Bug type<select value={form.bug_type} onChange={(event) => update('bug_type', event.target.value)} disabled={busy}>{BUG_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Frequency<select value={form.frequency} onChange={(event) => update('frequency', event.target.value)} disabled={busy}>{FREQUENCIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        </div>

        <label>Short title<input required minLength="4" maxLength="140" value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="Market still shows the previous location" disabled={busy} /></label>
        <label>Description<textarea required minLength="10" maxLength="8000" rows="4" value={form.description} onChange={(event) => update('description', event.target.value)} placeholder="Explain what happened in your own words." disabled={busy} /></label>
        <label>Steps to reproduce<textarea maxLength="8000" rows="4" value={form.steps_to_reproduce} onChange={(event) => update('steps_to_reproduce', event.target.value)} placeholder="1. Open Trade at Mt. Gate&#10;2. Travel to Arcata&#10;3. Open Trade again" disabled={busy} /></label>
        <div className="bug-result-grid">
          <label>Expected result<textarea maxLength="8000" rows="3" value={form.expected_result} onChange={(event) => update('expected_result', event.target.value)} placeholder="The Arcata market should appear." disabled={busy} /></label>
          <label>Actual result<textarea maxLength="8000" rows="3" value={form.actual_result} onChange={(event) => update('actual_result', event.target.value)} placeholder="The Mt. Gate market remained on screen." disabled={busy} /></label>
        </div>

        <label className="screenshot-picker"><span><Camera size={17} /><strong>Optional screenshot</strong><small>PNG, JPEG, or WebP · maximum 10 MB · location metadata is stripped by the hub</small></span><input type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseScreenshot} disabled={busy} /></label>
        {previewUrl && <div className="bug-screenshot-preview"><img src={previewUrl} alt="Selected bug screenshot preview" /><button type="button" className="mini-button" onClick={() => { setScreenshot(null); URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }}>Remove</button></div>}

        <div className="bug-diagnostic-summary">
          <FileWarning size={18} />
          <div><strong>Safe diagnostics attached automatically</strong><p>Client {diagnosticsPreview.client} · {diagnosticsPreview.interface}/{diagnosticsPreview.theme} · {diagnosticsPreview.location} · run {diagnosticsPreview.run} rev {diagnosticsPreview.revision}</p><small>No passphrase, session token, invite code, or mail secret is included.</small></div>
        </div>

        <button className="btn buy bug-submit-button" type="submit" disabled={busy}><Send size={16} /> {busy ? 'Submitting…' : 'Submit Bug Report'}</button>
      </form>

      {error && <div className="form-error" role="alert">{error}</div>}
      {success && <div className="bug-submit-success"><CheckCircle2 /><div><strong>Bug report submitted</strong><p>Reference: <code>{success.reference}</code></p></div></div>}

      <div className="my-bug-reports">
        <div className="bug-list-heading"><h4>My Bug Reports</h4><small>{reports.length} report{reports.length === 1 ? '' : 's'}</small></div>
        {reportsBusy ? <p>Loading reports…</p> : reports.length ? reports.map((report) => (
          <article key={report.reference} className="my-bug-card">
            <div><code>{report.reference}</code><span className={`bug-status status-${report.status}`}>{STATUS_LABELS[report.status] || report.status}</span></div>
            <strong>{report.title}</strong>
            <small>{new Date(report.created_at).toLocaleString()} · {report.section.replaceAll('_', ' ')}</small>
            {report.admin_note && <p><b>Admin:</b> {report.admin_note}</p>}
          </article>
        )) : <p className="fine-print">No bug reports submitted from this account yet.</p>}
      </div>
    </section>
  );
}
